import React, { useMemo } from 'react';
import { Statistic } from '../components/StockTable';
import { TickerDisplayData } from '../types/ticker';
import { DAILY_STATISTICS, formatPrice, formatChange, formatChangePercent, formatVolumeWithArrow, getLatestLabel, getOpenLabel } from '../utils/dailyTabUtils';

export interface UseDailyTabStatisticsOptions {
  marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null;
  isEditingTickers?: boolean;
  cachedLatestLabel?: string; // Optional cached label to avoid flickering during market status loading
  cachedOpenLabel?: string; // Optional cached open label to avoid flickering during market status loading
  marketOpenTimestamp?: string | null; // Server-provided timestamp when market changed to 'open'
  marketCloseTimestamp?: string | null; // Server-provided timestamp when market changed from 'open' to 'closed'/'after-hours'
  marketPreMarketTimestamp?: string | null; // Server-provided timestamp when market changed to 'pre-market'
}

/**
 * Hook to get visible statistics for Daily tab based on market status
 */
export const useDailyTabStatistics = (options: UseDailyTabStatisticsOptions): Statistic[] => {
  const { marketStatus, isEditingTickers = false, cachedLatestLabel, cachedOpenLabel, marketOpenTimestamp, marketCloseTimestamp, marketPreMarketTimestamp } = options;

  return useMemo(() => {
    const getLatestLabelValue = () => {
      if (isEditingTickers) {
        return 'Latest/Last Close';
      }
      // Use cached label if provided to avoid flickering during market status loading
      if (cachedLatestLabel !== undefined) {
        return cachedLatestLabel;
      }
      return getLatestLabel(marketStatus, marketOpenTimestamp, marketCloseTimestamp);
    };

    const getOpenLabelValue = () => {
      if (isEditingTickers) {
        return 'Open';
      }
      // Use cached label if provided to avoid flickering during market status loading
      if (cachedOpenLabel !== undefined) {
        return cachedOpenLabel;
      }
      return getOpenLabel(marketStatus, marketOpenTimestamp, marketCloseTimestamp);
    };

    // In edit mode, show statistics based on market status
    if (isEditingTickers) {
      const baseStatistics = DAILY_STATISTICS.filter(stat => 
        !['preMarket', 'preMarketChange', 'preMarketChangePercent', 'afterHours', 'afterHoursChange', 'afterHoursChangePercent'].includes(stat.key)
      ).map(stat => {
        if (stat.key === 'latest') {
          return { ...stat, label: getLatestLabelValue() };
        }
        if (stat.key === 'open') {
          return { ...stat, label: getOpenLabelValue() };
        }
        return stat;
      });

      if (marketStatus === 'pre-market') {
        // Check if market just entered pre-market within 15 minutes - if so, don't show pre-market stats yet
        // (prices may not be available yet due to 15-minute delay)
        if (marketPreMarketTimestamp) {
          const preMarketTimestamp = new Date(marketPreMarketTimestamp).getTime();
          if (!isNaN(preMarketTimestamp)) {
            const now = Date.now();
            const timeSincePreMarketMs = now - preMarketTimestamp;
            const timeSincePreMarketMinutes = timeSincePreMarketMs / (1000 * 60);
            // If within 15 minutes of entering pre-market, don't show pre-market stats yet
            if (timeSincePreMarketMinutes <= 15) {
              return baseStatistics;
            }
          }
        }
        // Show pre-market stats if more than 15 minutes have passed since entering pre-market
        const preMarketStats = DAILY_STATISTICS.filter(stat => 
          ['preMarket', 'preMarketChange', 'preMarketChangePercent'].includes(stat.key)
        );
        return [...preMarketStats, ...baseStatistics];
      } else if (marketStatus === 'after-hours') {
        // Check if market just closed within 15 minutes - if so, don't show after-hours stats yet
        // (prices are still from when market was open due to 15-minute delay)
        if (marketCloseTimestamp) {
          const closeTimestamp = new Date(marketCloseTimestamp).getTime();
          if (!isNaN(closeTimestamp)) {
            const now = Date.now();
            const timeSinceCloseMs = now - closeTimestamp;
            const timeSinceCloseMinutes = timeSinceCloseMs / (1000 * 60);
            // If within 15 minutes of close, don't show after-hours stats yet
            if (timeSinceCloseMinutes <= 15) {
              return baseStatistics;
            }
          }
        }
        // Show after-hours stats if more than 15 minutes have passed since market closed
        const afterHoursStats = DAILY_STATISTICS.filter(stat => 
          ['afterHours', 'afterHoursChange', 'afterHoursChangePercent'].includes(stat.key)
        );
        return [...afterHoursStats, ...baseStatistics];
      }

      return baseStatistics;
    }

    // In normal mode, when market status is loading, show only base statistics (no pre-market/after-hours)
    // This prevents flashing of pre-market/after-hours stats when market status is actually 'open'
    if (marketStatus === null) {
      return DAILY_STATISTICS.filter(stat => 
        !['preMarket', 'preMarketChange', 'preMarketChangePercent', 'afterHours', 'afterHoursChange', 'afterHoursChangePercent'].includes(stat.key)
      ).map(stat => {
        if (stat.key === 'latest') {
          return { ...stat, label: getLatestLabelValue() };
        }
        if (stat.key === 'open') {
          return { ...stat, label: getOpenLabelValue() };
        }
        return stat;
      });
    }

    // Filter statistics based on market status
    const baseStatistics = DAILY_STATISTICS.filter(stat => 
      !['preMarket', 'preMarketChange', 'preMarketChangePercent', 'afterHours', 'afterHoursChange', 'afterHoursChangePercent'].includes(stat.key)
    ).map(stat => {
      if (stat.key === 'latest') {
        return { ...stat, label: getLatestLabelValue() };
      }
      if (stat.key === 'open') {
        return { ...stat, label: getOpenLabelValue() };
      }
      return stat;
    });

    if (marketStatus === 'pre-market') {
      // Check if market just entered pre-market within 15 minutes - if so, don't show pre-market stats yet
      // (prices may not be available yet due to 15-minute delay)
      if (marketPreMarketTimestamp) {
        const preMarketTimestamp = new Date(marketPreMarketTimestamp).getTime();
        if (!isNaN(preMarketTimestamp)) {
          const now = Date.now();
          const timeSincePreMarketMs = now - preMarketTimestamp;
          const timeSincePreMarketMinutes = timeSincePreMarketMs / (1000 * 60);
          // If within 15 minutes of entering pre-market, don't show pre-market stats yet
          if (timeSincePreMarketMinutes <= 15) {
            return baseStatistics;
          }
        }
      }
      // Show pre-market stats if more than 15 minutes have passed since entering pre-market
      const preMarketStats = DAILY_STATISTICS.filter(stat => 
        ['preMarket', 'preMarketChange', 'preMarketChangePercent'].includes(stat.key)
      );
      return [...preMarketStats, ...baseStatistics];
    } else if (marketStatus === 'after-hours') {
      // Check if market just closed within 15 minutes - if so, don't show after-hours stats yet
      // (prices are still from when market was open due to 15-minute delay)
      if (marketCloseTimestamp) {
        const closeTimestamp = new Date(marketCloseTimestamp).getTime();
        if (!isNaN(closeTimestamp)) {
          const now = Date.now();
          const timeSinceCloseMs = now - closeTimestamp;
          const timeSinceCloseMinutes = timeSinceCloseMs / (1000 * 60);
          // If within 15 minutes of close, don't show after-hours stats yet
          if (timeSinceCloseMinutes <= 15) {
            return baseStatistics;
          }
        }
      }
      // Show after-hours stats if more than 15 minutes have passed since market closed
      const afterHoursStats = DAILY_STATISTICS.filter(stat => 
        ['afterHours', 'afterHoursChange', 'afterHoursChangePercent'].includes(stat.key)
      );
      return [...afterHoursStats, ...baseStatistics];
    }

    // For 'open' or 'closed' status, show regular statistics only
    return baseStatistics;
  }, [marketStatus, isEditingTickers, cachedLatestLabel, cachedOpenLabel, marketOpenTimestamp, marketCloseTimestamp, marketPreMarketTimestamp]);
};

/**
 * Format statistic value for Daily tab
 */
export const formatDailyStatisticValue = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: {
    marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null;
    isEditingTickers?: boolean;
  }
): React.ReactNode => {
  const { marketStatus, isEditingTickers = false } = options;
  const emptyPlaceholder = React.createElement('span', { className: 'text-gray-400 dark:text-text-secondary' }, '-');

  switch (statisticKey) {
    case 'preMarket':
      // In edit mode, show "-" if not in pre-market status
      if (isEditingTickers && marketStatus !== 'pre-market') {
        return emptyPlaceholder;
      }
      return ticker.preMarket ? formatPrice(ticker.preMarket) : emptyPlaceholder;
    case 'preMarketChange':
      if (isEditingTickers && marketStatus !== 'pre-market') {
        return emptyPlaceholder;
      }
      return ticker.preMarketChange !== undefined ? formatChange(ticker.preMarketChange) : emptyPlaceholder;
    case 'preMarketChangePercent':
      if (isEditingTickers && marketStatus !== 'pre-market') {
        return emptyPlaceholder;
      }
      return ticker.preMarketChangePercent !== undefined ? formatChangePercent(ticker.preMarketChangePercent) : emptyPlaceholder;
    case 'afterHours':
      if (isEditingTickers && marketStatus !== 'after-hours') {
        return emptyPlaceholder;
      }
      return ticker.afterHours ? formatPrice(ticker.afterHours) : emptyPlaceholder;
    case 'afterHoursChange':
      if (isEditingTickers && marketStatus !== 'after-hours') {
        return emptyPlaceholder;
      }
      return ticker.afterHoursChange !== undefined ? formatChange(ticker.afterHoursChange) : emptyPlaceholder;
    case 'afterHoursChangePercent':
      if (isEditingTickers && marketStatus !== 'after-hours') {
        return emptyPlaceholder;
      }
      return ticker.afterHoursChangePercent !== undefined ? formatChangePercent(ticker.afterHoursChangePercent) : emptyPlaceholder;
    case 'latest':
      return formatPrice(ticker.latest);
    case 'change':
      return formatChange(ticker.change);
    case 'changePercent':
      return formatChangePercent(ticker.changePercent);
    case 'open':
      return formatPrice(ticker.open);
    case 'high':
      return formatPrice(ticker.high);
    case 'low':
      return formatPrice(ticker.low);
    case 'volume':
      return formatVolumeWithArrow(ticker, marketStatus);
    default:
      return emptyPlaceholder;
  }
};

/**
 * Get statistic color for Daily tab
 */
export const getDailyStatisticColor = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: {
    marketStatus: 'open' | 'pre-market' | 'after-hours' | 'closed' | null;
    isEditingTickers?: boolean;
  }
): string => {
  const { marketStatus, isEditingTickers = false } = options;

  // Volume uses arrow icons instead of text colors
  if (statisticKey === 'volume') {
    return 'text-text-light-neutral dark:text-text-secondary';
  }
  
  // Check if the value would be displayed as "-" and return neutral color
  const formatValue = (key: string) => {
    switch (key) {
      case 'preMarket':
        if (isEditingTickers && marketStatus !== 'pre-market') return '-';
        return ticker.preMarket ? formatPrice(ticker.preMarket) : '-';
      case 'preMarketChange':
        if (isEditingTickers && marketStatus !== 'pre-market') return '-';
        return ticker.preMarketChange !== undefined ? formatChange(ticker.preMarketChange) : '-';
      case 'preMarketChangePercent':
        if (isEditingTickers && marketStatus !== 'pre-market') return '-';
        return ticker.preMarketChangePercent !== undefined ? formatChangePercent(ticker.preMarketChangePercent) : '-';
      case 'afterHours':
        if (isEditingTickers && marketStatus !== 'after-hours') return '-';
        return ticker.afterHours ? formatPrice(ticker.afterHours) : '-';
      case 'afterHoursChange':
        if (isEditingTickers && marketStatus !== 'after-hours') return '-';
        return ticker.afterHoursChange !== undefined ? formatChange(ticker.afterHoursChange) : '-';
      case 'afterHoursChangePercent':
        if (isEditingTickers && marketStatus !== 'after-hours') return '-';
        return ticker.afterHoursChangePercent !== undefined ? formatChangePercent(ticker.afterHoursChangePercent) : '-';
      default:
        return '';
    }
  };
  
  // If the value would be "-", return neutral color
  if (formatValue(statisticKey) === '-') {
    return 'text-text-light-neutral dark:text-text-secondary';
  }
  
  // Pre-market statistics: compare against latest price (current day's close)
  if (statisticKey === 'preMarket') {
    const isPositive = (ticker?.preMarket ?? 0) >= (ticker?.latest ?? 0);
    return isPositive ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
  }
  if (statisticKey === 'preMarketChange') {
    const isPositive = (ticker?.preMarketChange ?? 0) >= 0;
    return isPositive ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
  }
  if (statisticKey === 'preMarketChangePercent') {
    const isPositive = (ticker?.preMarketChangePercent ?? 0) >= 0;
    return isPositive ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
  }
  
  // After-hours statistics: compare against latest price (current day's close)
  if (statisticKey === 'afterHours') {
    const isPositive = (ticker?.afterHours ?? 0) >= (ticker?.latest ?? 0);
    return isPositive ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
  }
  if (statisticKey === 'afterHoursChange') {
    const isPositive = (ticker?.afterHoursChange ?? 0) >= 0;
    return isPositive ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
  }
  if (statisticKey === 'afterHoursChangePercent') {
    const isPositive = (ticker?.afterHoursChangePercent ?? 0) >= 0;
    return isPositive ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
  }
  
  // Regular statistics
  const isPositive =
    statisticKey === 'latest'
      ? (ticker?.latest ?? 0) >= (ticker?.prevClose ?? 0)
      : statisticKey === 'change'
      ? (ticker?.change ?? 0) >= 0
      : statisticKey === 'changePercent'
      ? (ticker?.changePercent ?? 0) >= 0
      : statisticKey === 'open'
      ? (ticker?.open ?? 0) >= (ticker?.prevClose ?? 0)
      : statisticKey === 'high'
      ? (ticker?.high ?? 0) >= (ticker?.prevClose ?? 0)
      : statisticKey === 'low'
      ? (ticker?.low ?? 0) >= (ticker?.prevClose ?? 0)
      : false;
  
  return isPositive ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
};

